import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { DncEntry } from '@marnic/shared';
import { formatUKForDisplay } from '@marnic/shared';
import { relativeTime } from '../lib/utils';

interface DncResponse {
  total: number;
  items: DncEntry[];
}

interface AuditEvent {
  id: string;
  action: string;
  target: string | null;
  severity: string;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string } | null;
  detail: Record<string, unknown>;
}
interface AuditResponse {
  total: number;
  items: AuditEvent[];
}

export function Compliance(): JSX.Element {
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');

  const dnc = useQuery({
    queryKey: ['compliance', 'dnc'],
    queryFn: () => api.get<DncResponse>('/compliance/dnc'),
  });
  const audit = useQuery({
    queryKey: ['compliance', 'audit'],
    queryFn: () => api.get<AuditResponse>('/compliance/audit?limit=50'),
  });

  const addDnc = useMutation({
    mutationFn: () => api.post('/compliance/dnc', { phone, reason: reason || 'manual', source: 'internal' }),
    onSuccess: () => {
      setPhone('');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['compliance', 'dnc'] });
    },
  });
  const removeDnc = useMutation({
    mutationFn: (id: string) => api.delete(`/compliance/dnc/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance', 'dnc'] }),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Compliance</h1>
        <p className="text-sm text-ink-500">DNC list, TPS lookups, and audit trail.</p>
      </header>

      <section className="card p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Add to DNC</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
          <input className="input" placeholder="UK phone (e.g. +447700900123)" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <input className="input" placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="button" className="btn-primary" onClick={() => addDnc.mutate()} disabled={!phone || addDnc.isPending}>
            {addDnc.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
      </section>

      <section className="card overflow-hidden">
        <header className="border-b border-ink-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">DNC list</h2>
          <span className="text-xs text-ink-500">{dnc.data?.total ?? 0} entries</span>
        </header>
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Source</th>
              <th className="px-4 py-2">Reason</th>
              <th className="px-4 py-2">Added</th>
              <th className="px-4 py-2">Expires</th>
              <th className="px-4 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {dnc.data?.items.map((e) => (
              <tr key={e.id} className="border-t border-ink-100">
                <td className="px-4 py-2 font-mono">{formatUKForDisplay(e.phoneE164)}</td>
                <td className="px-4 py-2 capitalize">{e.source}</td>
                <td className="px-4 py-2">{e.reason}</td>
                <td className="px-4 py-2 text-ink-500">{relativeTime(e.addedAt)}</td>
                <td className="px-4 py-2 text-ink-500">{e.expiresAt ? relativeTime(e.expiresAt) : 'never'}</td>
                <td className="px-4 py-2">
                  <button type="button" className="btn-ghost text-xs" onClick={() => removeDnc.mutate(e.id)}>Remove</button>
                </td>
              </tr>
            ))}
            {dnc.data?.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-ink-500">No entries.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </section>

      <section className="card overflow-hidden">
        <header className="border-b border-ink-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Audit trail</h2>
          <span className="text-xs text-ink-500">latest 50</span>
        </header>
        <div className="overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
            <tr>
              <th className="px-4 py-2">When</th>
              <th className="px-4 py-2">Who</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Target</th>
              <th className="px-4 py-2">Severity</th>
            </tr>
          </thead>
          <tbody>
            {audit.data?.items.map((e) => (
              <tr key={e.id} className="border-t border-ink-100">
                <td className="px-4 py-2 text-ink-500">{relativeTime(e.createdAt)}</td>
                <td className="px-4 py-2">{e.user ? `${e.user.firstName} ${e.user.lastName}` : 'system'}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.target ?? '–'}</td>
                <td className="px-4 py-2">
                  <span className={`badge ${e.severity === 'critical' ? 'bg-red-100 text-red-700' : e.severity === 'elevated' ? 'bg-amber-100 text-amber-700' : 'bg-ink-100 text-ink-700'}`}>
                    {e.severity}
                  </span>
                </td>
              </tr>
            ))}
            {audit.data?.items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-ink-500">No events yet.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}
